import React, { useState } from 'react'

import { Autocomplete, Box, Chip, LinearProgress, Link, MenuItem, TextField, Tooltip, Typography } from '@mui/material'
import { useCustomerDeliveries, useDeliveryEvents } from 'features/customers/hooks/useCustomerDeliveries'
import { useCustomerId } from 'features/customers/hooks/useCustomerId'
import { useCustomerWorkflows } from 'features/customers/hooks/useCustomerWorkflows'
import { keyBy, sortBy } from 'lodash'
import { Link as RouterLink } from 'react-router-dom'
import { UrlProvider } from 'urls'

import { DELIVERY_OUTCOME_FILTERS, type DeliveryOutcomeFilter, type DeliveryRecord } from '@albacross/types'

import Table from 'components/table/Table'
import type { ColumnConfig } from 'components/table/types'

import { formatUtc } from '../contacts/formatUtc'
import { useDebouncedSearch } from '../contacts/useDebouncedSearch'
import { usePageOf } from '../contacts/usePageOf'
import { detailLines, eventText, integrationName, outcomeColour } from './deliveryLabels'

type Workflow = { id: number; type: string; name: string }

const ALL = 'all'

const LAST_SENT_NOTE =
  'Latest send of this company to this workflow (from the worker history), not necessarily this version.'

const workflowLabel = ({ id, type, name }: Workflow) => `#${id} [${integrationName(type)}] ${name}`

// ALBA-CH-geschenkkoerbe~ch is geschenkkoerbe.ch; number- and name-based uuids (ALBA-NL-MAGLR) have no ~ and stay.
const LEAD_DOMAIN = /^ALBA-[A-Z]+-(.+~.+)$/

const leadLabel = (leadUuid: string) => leadUuid.match(LEAD_DOMAIN)?.[1].replaceAll('~', '.') ?? leadUuid

// A minute early: the shadow writes the row fire-and-forget, so audit events can precede it.
const auditFrom = (createdAt: string) => new Date(Date.parse(createdAt) - 60_000).toISOString()

// The decision is the first event, so it stands in when the list has no latest event.
const outcomeEvent = ({ latest_event, decision, denial_reason }: DeliveryRecord) =>
  latest_event ?? { type: decision, reason: denial_reason, detail: null }

const Outcome = ({ delivery }: { delivery: DeliveryRecord }) => {
  const event = outcomeEvent(delivery)
  return <Chip color={outcomeColour(event)} label={eventText(event)} size="small" />
}

const auditLink = (customerId: string | undefined, { lead_uuid, workflow_id, created_at }: DeliveryRecord) =>
  `${UrlProvider.getUrl('customer', { id: customerId, tab: 'audit' })}?${new URLSearchParams({
    uuid: lead_uuid,
    workflow_id: String(workflow_id),
    date_from: auditFrom(created_at),
  })}`

const Timeline = ({ delivery }: { delivery: DeliveryRecord }) => {
  const customerId = useCustomerId()
  const { data: events, error } = useDeliveryEvents(delivery.id)

  const body = () => {
    if (error) return <Typography variant="body2">Could not load the timeline.</Typography>
    if (!events) return <Typography variant="body2">Loading the timeline…</Typography>
    if (!events.length) return <Typography variant="body2">No events recorded.</Typography>
    return (
      <Box component="ol" sx={{ m: 0, pl: 3 }}>
        {events.map(event => (
          <li key={event.id}>
            <Typography variant="body2">
              {formatUtc(event.created_at)} {eventText(event)}
            </Typography>
            {detailLines(event).map(line => (
              <Typography color="text.secondary" component="div" key={line} variant="caption">
                {line}
              </Typography>
            ))}
          </li>
        ))}
      </Box>
    )
  }

  return (
    <Box
      aria-label="Timeline"
      component="section"
      data-testid={`delivery-timeline-${delivery.id}`}
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box
        component="dl"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'max-content 1fr',
          columnGap: 2,
          m: 0,
          typography: 'body2',
          '& dd': { m: 0 },
        }}>
        <dt>Integration</dt>
        <dd>{integrationName(delivery.integration)}</dd>
        <dt>Deliver at (UTC)</dt>
        <dd>{formatUtc(delivery.deliver_at)}</dd>
        <dt>Times received</dt>
        <dd>{delivery.seen_count}</dd>
        <dt>Contacts</dt>
        <dd>{delivery.wants_contacts ? 'Wanted' : 'Not wanted'}</dd>
        <Box component="dt" sx={{ color: 'text.secondary' }}>
          Version
        </Box>
        <Box component="dd" sx={{ color: 'text.secondary' }}>
          {delivery.lead_version}
        </Box>
      </Box>
      {body()}
      {/* Stopgap until the integration runs through the ledger (stage 3/4). */}
      <Link component={RouterLink} to={auditLink(customerId, delivery)} variant="body2">
        After hand-off: see Audit
      </Link>
    </Box>
  )
}

const columnsOf = (workflows: Record<number, Workflow>): ColumnConfig<DeliveryRecord>[] => [
  { title: 'Lead', render: ({ lead_uuid }) => leadLabel(lead_uuid) },
  { title: 'Outcome', render: delivery => <Outcome delivery={delivery} /> },
  {
    title: 'Workflow',
    render: ({ workflow_id }) => {
      const workflow = workflows[workflow_id]
      return workflow ? workflowLabel(workflow) : `#${workflow_id}`
    },
  },
  {
    title: (
      <Tooltip title={LAST_SENT_NOTE}>
        <span>Last sent to this workflow (UTC)</span>
      </Tooltip>
    ),
    noBreak: true,
    render: ({ last_sent_at }) => (last_sent_at ? formatUtc(last_sent_at) : 'Never sent'),
  },
  { title: 'Received (UTC)', noBreak: true, render: ({ created_at }) => formatUtc(created_at) },
]

const CustomerDeliveries = () => {
  const { text, setText, search } = useDebouncedSearch()
  const [selectedWorkflows, setSelectedWorkflows] = useState<Workflow[]>([])
  const [outcome, setOutcome] = useState<DeliveryOutcomeFilter | typeof ALL>(ALL)
  const [pageSize, setPageSize] = useState(25)
  const workflowIds = selectedWorkflows.map(({ id }) => id).join(',')
  const { page, setPage } = usePageOf(JSON.stringify([search, outcome, workflowIds, pageSize]))

  const { data: workflowsData } = useCustomerWorkflows()
  const workflows: Workflow[] = sortBy(workflowsData?.workflows ?? [], 'type')

  const {
    data: deliveries,
    isFetching,
    error,
  } = useCustomerDeliveries({
    ...(search ? { lead: search } : {}),
    ...(outcome === ALL ? {} : { outcome }),
    ...(workflowIds ? { workflow_id: workflowIds } : {}),
    page: page + 1,
    page_size: pageSize,
  })

  const toolbar = (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <TextField
        label="Lead"
        onChange={e => setText(e.target.value)}
        placeholder="Domain, URL or lead uuid"
        size="small"
        sx={{ minWidth: 320 }}
        value={text}
      />
      <TextField
        label="Outcome"
        onChange={e => setOutcome(e.target.value as DeliveryOutcomeFilter | typeof ALL)}
        select
        size="small"
        sx={{ minWidth: 280 }}
        value={outcome}>
        <MenuItem value={ALL}>All</MenuItem>
        {Object.entries(DELIVERY_OUTCOME_FILTERS).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      <Autocomplete
        disableCloseOnSelect
        getOptionLabel={workflowLabel}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        multiple
        onChange={(_event, selected) => setSelectedWorkflows(selected)}
        options={workflows}
        renderInput={params => (
          <TextField {...params} label="Workflows" placeholder={selectedWorkflows.length ? '' : 'All'} />
        )}
        size="small"
        sx={{ minWidth: 320 }}
        value={selectedWorkflows}
      />
    </Box>
  )

  return (
    <section aria-label="Deliveries" data-testid="deliveries">
      <LinearProgress aria-label="Loading deliveries" style={{ visibility: isFetching ? 'visible' : 'hidden' }} />
      <Table<DeliveryRecord>
        config={columnsOf(keyBy(workflows, 'id'))}
        count={deliveries?.total}
        data={deliveries?.rows ?? []}
        error={error ? 'Could not load the deliveries' : undefined}
        loading={isFetching && !deliveries}
        minWidth={900}
        onChangePage={setPage}
        onChangeRowsPerPage={value => setPageSize(Number(value))}
        page={page}
        renderDetailView={(delivery: DeliveryRecord) => <Timeline delivery={delivery} />}
        rowsPerPage={pageSize}
        size="small"
        title="Deliveries"
        toolbar={toolbar}
      />
    </section>
  )
}

export default CustomerDeliveries
